import { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* Motion variants: stagger vào mượt hơn */
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.04, staggerChildren: 0.025 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 6, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.18 } },
};

const ImgIcon = ({ src, alt, priority = false }) => {
  const width = 72;
  const height = 72;

  const src80 = `${src}?w=80&h=80&fit=contain&format=auto`;
  const src160 = `${src}?w=160&h=160&fit=contain&format=auto`;
  const src320 = `${src}?w=320&h=320&fit=contain&format=auto`;

  return (
    <div
      className="
        relative mt-1 flex h-[78px] w-[78px] items-center justify-center
        rounded-xl bg-white p-2
        shadow-[0_8px_20px_rgba(1,85,248,0.08)]
      "
    >
      <img
        src={src80}
        srcSet={`${src80} 80w, ${src160} 160w, ${src320} 320w`}
        sizes="(max-width: 640px) 80px, 80px"
        alt={alt}
        width={width}
        height={height}
        decoding="async"
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        className="h-full w-full rounded-lg object-contain object-center transition-opacity duration-200 opacity-0"
        onLoad={(e) => {
          e.currentTarget.classList.remove("opacity-0");
        }}
      />
    </div>
  );
};

const FilterButtons = ({ category = [], onSelect }) => {
  const handleKeyActive = useCallback(
    (e, cat) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect?.(cat);
      }
    },
    [onSelect],
  );

  return (
    <AnimatePresence mode="wait">
      <motion.ul
        role="list"
        initial="hidden"
        animate="show"
        variants={containerVariants}
        className="
          grid w-full gap-4 sm:gap-5
          grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5
        "
      >
        {category.map((cat) => (
          <motion.li
            role="listitem"
            key={cat.id}
            variants={itemVariants}
            layout
            className="h-full"
          >
            <button
              type="button"
              onClick={() => onSelect?.(cat)}
              onKeyDown={(e) => handleKeyActive(e, cat)}
              title={cat.name}
              aria-label={`Mở danh mục ${cat.name}`}
              className="
                group relative flex h-full w-full flex-col items-center justify-start
                gap-3 rounded-xl border border-[#B7D4FF] bg-white p-4 shadow-sm
                transition-[border-color,box-shadow,transform] duration-200
                hover:-translate-y-0.5 hover:border-[#7DB5FF] hover:shadow-md
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0155F8]/25 focus-visible:ring-offset-2
              "
            >
              <ImgIcon src={cat.image} alt={`Hình danh mục ${cat.name}`} />

              <span
                className="
                  mt-1 line-clamp-2 max-w-[95%] text-center text-[13.5px]
                  font-sf-bold text-slate-800 group-hover:text-[#0155F8]
                "
                style={{
                  fontFamily: '"SF Pro Display Bold", sans-serif',
                  fontWeight: 700,
                }}
              >
                {cat.name}
              </span>

              <span
                aria-hidden="true"
                className="
                  pointer-events-none absolute inset-0 rounded-xl opacity-0
                  transition-opacity duration-200 group-hover:opacity-100
                  [background:radial-gradient(90%_70%_at_50%_0%,rgba(1,85,248,0.08),transparent_65%)]
                "
              />
            </button>
          </motion.li>
        ))}
      </motion.ul>
    </AnimatePresence>
  );
};

export default FilterButtons;
